import type {
    ComponentRequisitionPayload,
    ComponentRequisitionResponse,
} from '@/types/apps/component-requisition.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface ComponentRequisitionServiceInterface {}

type ComponentRequisitionListResponse = ApiResponse<
    PaginatedResult<ComponentRequisitionResponse>
>;

export class ComponentRequisitionService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/PartRequisitions',
        detail: (id: string) => `/api/PartRequisitions/${id}`,
        approve: (id: string) => `/api/PartRequisitions/approve/${id}`,
        reject: (id: string) => `/api/PartRequisitions/reject/${id}`,
    };

    public index(
        params?: QueryParams
    ): Promise<ComponentRequisitionListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ComponentRequisitionResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ComponentRequisitionPayload
    ): Promise<ApiResponse<ComponentRequisitionResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ComponentRequisitionPayload
    ): Promise<ApiResponse<ComponentRequisitionResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public approve(
        id: string
    ): Promise<ApiResponse<ComponentRequisitionResponse>> {
        return this.apiClient.putApi(this.endpoints.approve(id));
    }

    public reject(
        id: string
    ): Promise<ApiResponse<ComponentRequisitionResponse>> {
        return this.apiClient.putApi(this.endpoints.reject(id));
    }
}
